import { UI_THEME, getToneStyles, getButtonVariant } from './uiTheme.js'

// Wave 47x — uiStyles.js refactor.
//
// This module is the legacy `S` style object: ~80 named surfaces still
// referenced by tabs that haven't migrated to ui-kit primitives. The
// previous version inline-styled hardcoded hex (#0a0a0c, #141416),
// hardcoded radii (r.md/lg/xl all 8px — broken scale), and arbitrary
// transitions ('all 160ms ease'). Tabs that consume `S` thus drifted
// from the studio.css token system: changing a CSS variable changed
// nothing in `S`-styled tabs.
//
// This refactor:
//   1. Switches every color reference to var(--studio-*) so tabs pick
//      up token retunes automatically.
//   2. Uses the now-correct r.lg/xl/2xl scale.
//   3. Threads motion tokens through every transition.
//   4. Adds top-edge highlight + layered shadows on raised surfaces.
//   5. Switches the brand pill (genBtn, primaryActionSurface) to the
//      same gradient + cast-metal treatment used by .studio-button
//      .variant-primary so legacy and migrated buttons match.
//
// The exported shape stays identical (S.foo → CSSProperties or fn) so
// no consumer needs to change. This is a value-only refactor.

// ── Type Scale ───────────────────────────────────────────────────────────────
// Display:    28px / 800 / -0.04em   — hero titles, day counter
// Heading 1:  20px / 700 / -0.03em   — page titles
// Heading 2:  16px / 700 / -0.02em   — panel titles, card headers
// Body:       14px / 400 / -0.01em   — primary content
// Body Small: 13px / 400 / -0.01em   — secondary content, metadata
// Caption:    12px / 500 /  0.00em   — labels, timestamps
// System:     11px / 500 /  0.02em   — badges, pills, status
// Micro:      10px / 500 /  0.04em   — eyebrows, section dividers (DM Mono)

const c = UI_THEME.colors
const r = UI_THEME.radii
const sh = UI_THEME.shadow
const m = UI_THEME.motion
const e = UI_THEME.ease
const f = UI_THEME.fonts

// CSS variable references — these resolve at render time so retuning
// :root in studio.css changes the rendered output of inline-styled tabs
// without a JS rebuild. Falls back to the static hex if a browser fails
// to resolve the variable.
const v = {
  bg: 'var(--studio-bg, #0e0e10)',
  canvas: 'var(--studio-canvas, #141416)',
  surface0: 'var(--studio-surface0, #1c1c1f)',
  surface1: 'var(--studio-surface1, #222226)',
  text: 'var(--studio-text, #f0eee9)',
  // UI elevation 2026-06-09 — fallbacks track the lifted :root values so a
  // CSS-var resolution failure renders the new (legible) colors, not stale ones.
  textSecondary: 'var(--studio-text-secondary, #c7c2b8)',
  textTertiary: 'var(--studio-text-tertiary, #9a948a)',
  textGhost: 'var(--studio-text-ghost, #736d63)',
  // UI elevation 2026-06-09: label token for eyebrow/kicker roles
  label: 'var(--studio-label, #81786d)',
  border: 'var(--studio-border, rgba(180, 168, 150, 0.08))',
  borderStrong: 'var(--studio-border-strong, rgba(180, 168, 150, 0.14))',
  borderSubtle: 'var(--studio-border-subtle, rgba(180, 168, 150, 0.05))',
  brand: 'var(--studio-brand, #d4582d)',
  brandSoft: 'var(--studio-brand-soft, rgba(212, 88, 45, 0.12))',
  surfaceFlat: 'var(--surface-flat, rgba(255, 255, 255, 0.02))',
  surfaceRaised: 'var(--surface-raised, rgba(255, 255, 255, 0.035))',
  surfaceOverlay: 'var(--surface-overlay, rgba(255, 255, 255, 0.05))',
}

// ── Surface presets ──────────────────────────────────────────────────────────
const surfaceBase = {
  background: v.surface0,
  border: `1px solid ${v.border}`,
  borderRadius: r.xl,
  padding: 24,
  boxShadow: `${sh.card}, ${sh.highlightSoft}`,
}

const surfaceElevated = {
  background: v.surface1,
  border: `1px solid ${v.borderStrong}`,
  borderRadius: r.xl,
  padding: 24,
  boxShadow: `${sh.elevated}, ${sh.highlight}`,
}

const surfaceInset = {
  background: v.bg,
  border: `1px solid ${v.borderSubtle}`,
  borderRadius: r.lg,
  padding: 20,
}

// ── Styles ────────────────────────────────────────────────────────────────────

export const S = {
  // ── Layout shell ───────────────────────────────────────────────────────────
  app: {
    display: 'flex',
    height: '100vh',
    fontFamily: f.body,
    background: v.bg,
    color: v.text,
    overflow: 'hidden',
  },
  authShell: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    fontFamily: f.body,
    background: `radial-gradient(1200px 700px at 80% -10%, rgba(212, 88, 45, 0.06), transparent 60%), radial-gradient(900px 600px at -10% 30%, rgba(232, 152, 90, 0.035), transparent 60%), linear-gradient(180deg, #0c0c0e 0%, #0e0e10 50%, #0a0a0c 100%)`,
    color: v.text,
    boxSizing: 'border-box',
  },
  authCard: {
    ...surfaceElevated,
    width: 'min(100%, 420px)',
    display: 'grid',
    gap: 22,
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0) 50%), ${v.surface1}`,
  },
  authError: {
    background: c.errorSoft,
    border: `1px solid rgba(212, 114, 112, 0.32)`,
    borderRadius: r.md,
    padding: '10px 12px',
    fontSize: 13,
    color: c.error,
    lineHeight: 1.5,
  },
  sidebar: {
    width: 220,
    minWidth: 220,
    background: `linear-gradient(180deg, ${v.canvas} 0%, ${v.bg} 100%)`,
    borderRight: `1px solid ${v.border}`,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'inset -1px 0 0 rgba(255, 255, 255, 0.02)',
  },

  // ── Brand ──────────────────────────────────────────────────────────────────
  brand: {
    padding: '24px 20px 22px',
    borderBottom: `1px solid ${v.border}`,
  },
  // Brand wordmark — editorial serif at small size for the studio
  // signature feel. Stays brand-orange so the operator can locate
  // their workspace identity at a glance from any tab.
  brandName: {
    fontFamily: f.editorial,
    fontSize: 16,
    fontWeight: 600,
    color: v.brand,
    letterSpacing: '-0.015em',
    fontFeatureSettings: '"ss01" 1, "kern" 1',
    fontOpticalSizing: 'auto',
  },
  // UI elevation 2026-06-09: identity tag → sans, sentence case, label color
  brandSub: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.label,
    letterSpacing: '0em',
    textTransform: 'none',
    fontWeight: 500,
    marginTop: 6,
  },

  // ── Nav ────────────────────────────────────────────────────────────────────
  nav: { flex: 1, padding: '14px 12px', overflowY: 'auto' },
  navItem: (active) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '11px 14px',
    borderRadius: r.md,
    cursor: 'pointer',
    color: active ? v.text : v.textSecondary,
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    letterSpacing: '-0.005em',
    background: active
      ? 'linear-gradient(180deg, rgba(212, 88, 45, 0.16) 0%, rgba(212, 88, 45, 0.06) 100%)'
      : 'transparent',
    border: active
      ? `1px solid rgba(212, 88, 45, 0.32)`
      : '1px solid transparent',
    boxShadow: active
      ? `inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 0 0 3px rgba(212, 88, 45, 0.08), 0 2px 6px rgba(212, 88, 45, 0.16)`
      : 'none',
    width: '100%',
    textAlign: 'left',
    marginBottom: 2,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, color ${m.quick} ${e.out}, transform ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  navItemIndicator: (active) => ({
    width: 4,
    height: 22,
    borderRadius: r.pill,
    background: active
      ? 'linear-gradient(180deg, #e5683a 0%, #d4582d 50%, #c44e26 100%)'
      : 'transparent',
    boxShadow: active
      ? '0 0 12px rgba(212, 88, 45, 0.6), 0 0 4px rgba(212, 88, 45, 0.85)'
      : 'none',
    flexShrink: 0,
    transition: `background ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}, height ${m.quick} ${e.out}`,
  }),
  navIcon: (active) => ({
    fontSize: 13,
    width: 17,
    textAlign: 'center',
    color: active ? v.brand : v.textTertiary,
    transition: `color ${m.quick} ${e.out}`,
  }),
  // UI elevation 2026-06-09: nav badge → sans, metric label spec (sentence case)
  navBadge: {
    marginLeft: 'auto',
    background: 'transparent',
    color: v.textTertiary,
    fontSize: 11,
    fontWeight: 600,
    padding: '1px 0',
    fontFamily: f.body,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0em',
    textTransform: 'none',
  },
  // UI elevation 2026-06-09: nav warn → sans, metric label spec (sentence case)
  navWarn: {
    marginLeft: 'auto',
    fontFamily: f.body,
    fontSize: 11,
    color: c.warning,
    fontWeight: 600,
    letterSpacing: '0em',
    textTransform: 'none',
  },

  // ── Sidebar footer ─────────────────────────────────────────────────────────
  sideFooter: {
    padding: '16px 16px 20px',
    borderTop: `1px solid ${v.border}`,
    display: 'grid',
    gap: 8,
  },
  // UI elevation 2026-06-09: side footer title → sans, eyebrow spec (sentence case)
  sfTitle: {
    fontFamily: f.body,
    fontSize: 11,
    letterSpacing: '0em',
    textTransform: 'none',
    color: v.label,
    fontWeight: 600,
    marginBottom: 10,
  },
  sDay: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 0',
    fontSize: 11,
    color: v.textTertiary,
  },
  // UI elevation 2026-06-09: day name → sans, timestamp spec (no transform)
  sDayName: {
    fontFamily: f.body,
    fontSize: 11,
    width: 22,
    color: v.textTertiary,
    fontWeight: 500,
    letterSpacing: '0em',
    fontVariantNumeric: 'tabular-nums',
  },
  sideModuleCompact: {
    display: 'grid',
    gap: 6,
    padding: '4px 0 0',
  },
  // UI elevation 2026-06-09: side module title → sans, eyebrow spec (sentence case)
  sideModuleTitle: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.label,
    textTransform: 'none',
    letterSpacing: '0em',
    fontWeight: 600,
  },
  sideCompactRow: {
    display: 'grid',
    gap: 2,
  },
  sideHealthRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
  },

  // ── Main area ──────────────────────────────────────────────────────────────
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topbar: {
    height: 60,
    borderBottom: `1px solid ${v.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    gap: 16,
    flexShrink: 0,
    background: `linear-gradient(180deg, ${v.canvas} 0%, ${v.bg} 100%)`,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 150,
  },
  topTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.02em',
  },
  topSep: { color: v.textGhost },
  // UI elevation 2026-06-09: topbar sub → sans, eyebrow spec (sentence case)
  topSub: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.label,
    letterSpacing: '0em',
    textTransform: 'none',
    fontWeight: 600,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 28,
    background: v.bg,
  },
  topbarGroup: { display: 'flex', alignItems: 'center', gap: 10 },
  topbarStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    borderRadius: r.pill,
    border: `1px solid ${v.border}`,
    background: v.surfaceFlat,
    boxShadow: sh.highlightSoft,
  },
  topStatusDot: (tone) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    background:
      tone === 'healthy' ? c.success : tone === 'warning' ? c.warning : c.error,
    boxShadow:
      tone === 'healthy'
        ? '0 0 6px rgba(92, 186, 128, 0.5)'
        : tone === 'warning'
          ? '0 0 6px rgba(224, 181, 80, 0.5)'
          : '0 0 6px rgba(212, 114, 112, 0.5)',
  }),

  // ── Surfaces ───────────────────────────────────────────────────────────────
  surfaceBase,
  surfaceElevated,
  surfaceInset,

  card: { ...surfaceBase },
  consolePanel: { ...surfaceBase, overflow: 'hidden' },
  shellPanel: { ...surfaceBase },
  settingsPane: { ...surfaceBase },
  modeRail: { ...surfaceBase, height: 'fit-content' },
  heroBand: { ...surfaceBase, borderRadius: r['2xl'], marginBottom: 20 },
  resumeSurface: {
    ...surfaceBase,
    marginBottom: 20,
    overflow: 'hidden',
  },
  listPanel: {
    background: v.surface0,
    border: `1px solid ${v.border}`,
    borderRadius: r.xl,
    overflow: 'hidden',
    boxShadow: `${sh.card}, ${sh.highlightSoft}`,
  },
  inspectorPanel: {
    ...surfaceBase,
    minHeight: 520,
  },
  createOutputSurface: {
    ...surfaceBase,
    minHeight: 540,
    display: 'grid',
    alignContent: 'start',
  },

  // ── Hero surface (brand-tinted) ────────────────────────────────────────────
  // Wave 47x: gradient + ember glow + top highlight. Reads as the
  // most-elevated surface in the studio without needing a heavier
  // shadow that would compete with siblings on a busy dashboard.
  primaryActionSurface: {
    position: 'relative',
    display: 'grid',
    gap: 12,
    background: `radial-gradient(1100px 360px at 80% -20%, rgba(212, 88, 45, 0.08), transparent 60%), linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0) 30%), linear-gradient(135deg, ${v.surface0} 0%, ${v.canvas} 100%)`,
    border: `1px solid rgba(212, 88, 45, 0.22)`,
    borderRadius: r['2xl'],
    padding: 28,
    boxShadow: `${sh.card}, ${sh.highlight}, 0 0 40px rgba(212, 88, 45, 0.06)`,
    overflow: 'hidden',
  },

  // ── Content layout ─────────────────────────────────────────────────────────
  contentHead: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 24,
    gap: 16,
    flexWrap: 'wrap',
  },
  contentHeadCompact: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  contentTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: v.text,
    marginBottom: 2,
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
  },
  subText: {
    fontSize: 13,
    color: v.textSecondary,
    lineHeight: 1.55,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: v.textSecondary,
    marginBottom: 8,
  },
  // UI elevation 2026-06-09: operator note → sans, metric label spec (sentence case)
  operatorNote: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.textTertiary,
    letterSpacing: '0em',
    textTransform: 'none',
    fontWeight: 600,
  },
  // UI elevation 2026-06-09: panel eyebrow → sans, eyebrow spec (sentence case)
  panelEyebrow: {
    fontFamily: f.body,
    fontSize: 11,
    textTransform: 'none',
    letterSpacing: '0em',
    color: v.label,
    fontWeight: 600,
    marginBottom: 10,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.02em',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
  },
  // UI elevation 2026-06-09: page eyebrow → sans, eyebrow spec (sentence case)
  pageEyebrow: {
    fontFamily: f.body,
    fontSize: 11,
    textTransform: 'none',
    letterSpacing: '0em',
    color: v.label,
    fontWeight: 600,
    marginBottom: 10,
  },

  // ── Heroes ─────────────────────────────────────────────────────────────────
  // Editorial Fraunces serif on hero headlines — the studio gets a
  // clear "this is the marquee surface" voice without needing extra
  // size or weight. Optical sizing makes Fraunces sit right at 28px+.
  heroTitle: {
    fontFamily: f.editorial,
    fontSize: 32,
    lineHeight: 1.04,
    fontWeight: 600,
    color: v.text,
    letterSpacing: '-0.02em',
    marginBottom: 10,
    fontFeatureSettings: '"ss01" 1, "kern" 1',
  },
  heroTitleSmall: {
    fontFamily: f.editorial,
    fontSize: 22,
    lineHeight: 1.15,
    fontWeight: 600,
    color: v.text,
    letterSpacing: '-0.02em',
    marginBottom: 8,
    fontFeatureSettings: '"ss01" 1, "kern" 1',
  },
  heroMetaRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 16,
  },
  heroSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    alignContent: 'start',
  },
  // UI elevation 2026-06-09: hero stat padding 18×20 (desktop), 16×18 (mobile), gap 6px
  heroStat: {
    ...surfaceInset,
    padding: '18px 20px',
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.022) 0%, rgba(255, 255, 255, 0) 70%), ${v.bg}`,
    boxShadow: sh.highlightSoft,
    gap: 6,
    display: 'grid',
    '@media (max-width: 768px)': {
      padding: '16px 18px',
    },
  },
  // UI elevation 2026-06-09: hero stat label → sans, metric label spec (sentence case)
  heroStatLabel: {
    fontFamily: f.body,
    fontSize: 11,
    textTransform: 'none',
    letterSpacing: '0em',
    color: v.textTertiary,
    fontWeight: 600,
    marginBottom: 4,
  },
  heroStatValue: {
    fontSize: 17,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
  },
  heroStatMeta: {
    fontSize: 12,
    color: v.textSecondary,
    lineHeight: 1.5,
    marginTop: 4,
  },

  // ── Forms ──────────────────────────────────────────────────────────────────
  // Inputs now share the same focus glow + hover state as
  // .studio-field-control via the studio-input class. Inline styles
  // remain so legacy tabs render correctly during migration.
  input: {
    width: '100%',
    background: v.surfaceFlat,
    border: `1px solid ${v.borderStrong}`,
    borderRadius: r.md,
    padding: '10px 14px',
    color: v.text,
    fontFamily: f.body,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: `${sh.highlightSoft}, inset 0 1px 0 rgba(0, 0, 0, 0.08)`,
    transition: `border-color ${m.quick} ${e.out}, background-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  },
  textarea: (h) => ({
    width: '100%',
    background: v.surfaceFlat,
    border: `1px solid ${v.borderStrong}`,
    borderRadius: r.md,
    padding: '12px 14px',
    color: v.text,
    fontFamily: f.body,
    fontSize: 14,
    outline: 'none',
    minHeight: h,
    boxSizing: 'border-box',
    boxShadow: `${sh.highlightSoft}, inset 0 1px 0 rgba(0, 0, 0, 0.08)`,
    lineHeight: 1.55,
    transition: `border-color ${m.quick} ${e.out}, background-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  editArea: {
    width: '100%',
    background: v.bg,
    border: `1px solid ${v.borderSubtle}`,
    borderRadius: r.lg,
    padding: '20px 24px',
    minHeight: 280,
    fontSize: 14,
    lineHeight: 1.75,
    color: v.text,
    fontFamily: f.body,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  // schedInput stays mono: datetime-local renders as raw ISO-8601 string (§2.2 allowlist)
  schedInput: {
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.md,
    padding: '9px 12px',
    color: v.text,
    // UI elevation 2026-06-09 — sans + tabular-nums (datetime value, not raw code)
    fontFamily: f.body,
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
    boxShadow: sh.highlightSoft,
  },
  // Brand pill — matches .studio-button.variant-primary so legacy and
  // migrated buttons read as the same primary CTA.
  genBtn: (dis) => ({
    width: '100%',
    padding: '12px 18px',
    background: dis
      ? c.panelSoft
      : `linear-gradient(180deg, #de6235 0%, #d4582d 55%, #c44e26 100%)`,
    border: `1px solid ${dis ? v.border : 'rgba(212, 88, 45, 0.55)'}`,
    borderRadius: r.lg,
    color: dis ? v.textTertiary : '#1a0e05',
    fontFamily: f.body,
    fontSize: 13,
    fontWeight: 700,
    cursor: dis ? 'not-allowed' : 'pointer',
    marginTop: 14,
    letterSpacing: '-0.005em',
    boxShadow: dis ? 'none' : sh.brand,
    transition: `transform ${m.instant} ${e.snap}, box-shadow ${m.quick} ${e.out}, filter ${m.quick} ${e.out}`,
  }),
  // UI elevation 2026-06-09: char count → sans, metric label spec (sentence case)
  charCount: (over) => ({
    fontFamily: f.body,
    fontSize: 11,
    color: over ? c.error : v.textTertiary,
    textAlign: 'right',
    marginTop: 8,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    letterSpacing: '0em',
    textTransform: 'none',
  }),
  actionRow: { display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' },
  schedRow: {
    display: 'flex',
    gap: 10,
    marginTop: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },

  // ── Pillar selector ────────────────────────────────────────────────────────
  pillarGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  pillarBtn: (sel, color, rgb) => ({
    background: sel
      ? `linear-gradient(180deg, rgba(${rgb}, 0.14) 0%, rgba(${rgb}, 0.06) 100%)`
      : v.surfaceFlat,
    border: `1px solid ${sel ? `rgba(${rgb}, 0.4)` : v.border}`,
    borderRadius: r.lg,
    padding: '14px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: sel
      ? `0 0 16px rgba(${rgb}, 0.10), ${sh.highlight}`
      : sh.highlightSoft,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  // UI elevation 2026-06-09: pillar tag → sans, tag/chip spec (sentence case)
  pillarTag: (clr) => ({
    fontFamily: f.body,
    fontSize: 11,
    color: clr,
    marginBottom: 4,
    letterSpacing: '0em',
    textTransform: 'none',
    fontWeight: 600,
  }),
  pillarLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: v.text,
    marginBottom: 2,
    letterSpacing: '-0.01em',
  },
  // UI elevation 2026-06-09: pillar sched → sans, timestamp spec (no transform)
  pillarSched: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.textTertiary,
    fontWeight: 500,
    letterSpacing: '0em',
    fontVariantNumeric: 'tabular-nums',
  },

  // ── Content type tabs ──────────────────────────────────────────────────────
  pfTabs: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  pfTab: (sel) => ({
    flex: 1,
    minWidth: 120,
    padding: '10px 12px',
    background: sel ? c.accentSoft : v.surfaceFlat,
    border: `1px solid ${sel ? 'rgba(232, 152, 90, 0.3)' : v.border}`,
    borderRadius: r.md,
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: sel ? v.text : v.textTertiary,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, color ${m.quick} ${e.out}`,
  }),

  // ── Badges ─────────────────────────────────────────────────────────────────
  // UI elevation 2026-06-09: truth badge → sans, tag/chip spec (sentence case)
  truthBadge: (tone = 'neutral') => {
    const toneStyles = getToneStyles(tone)
    return {
      display: 'inline-flex',
      alignItems: 'center',
      fontFamily: f.body,
      fontSize: 11,
      fontWeight: 600,
      padding: '3px 8px',
      borderRadius: r.pill,
      background: toneStyles.background,
      color: toneStyles.color,
      border: `1px solid ${toneStyles.borderColor}`,
      letterSpacing: '0em',
      textTransform: 'none',
      boxShadow: sh.highlightSoft,
    }
  },

  // ── Post preview ───────────────────────────────────────────────────────────
  postPreview: {
    fontSize: 13,
    color: v.textTertiary,
    lineHeight: 1.55,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  // ── Empty states ───────────────────────────────────────────────────────────
  emptyState: {
    textAlign: 'left',
    padding: '32px 0',
    color: v.textTertiary,
  },
  emptyIcon: { fontSize: 22, marginBottom: 10, opacity: 0.4 },
  emptyLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: v.textSecondary,
  },

  // ── Calendar ───────────────────────────────────────────────────────────────
  calGrid: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 10 },
  // UI elevation 2026-06-09: calendar header → sans, eyebrow spec (sentence case)
  calHeader: {
    fontFamily: f.body,
    fontSize: 11,
    textTransform: 'none',
    color: v.label,
    textAlign: 'center',
    padding: '8px 0',
    letterSpacing: '0em',
    fontWeight: 600,
  },
  calDay: (today, empty) => ({
    background: empty ? v.canvas : v.surface0,
    border: `1px solid ${today ? `rgba(212, 88, 45, 0.4)` : v.border}`,
    borderRadius: r.lg,
    padding: '12px 10px',
    minHeight: 96,
    borderStyle: empty ? 'dashed' : 'solid',
    boxShadow: today
      ? `0 0 16px rgba(212, 88, 45, 0.10), ${sh.highlightSoft}`
      : empty
        ? 'none'
        : sh.highlightSoft,
    transition: `border-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  // UI elevation 2026-06-09: calendar day num → sans, timestamp spec (no transform)
  calDayNum: (today) => ({
    fontFamily: f.body,
    fontSize: 11,
    color: today ? c.brandText : v.textTertiary,
    marginBottom: 8,
    fontWeight: today ? 600 : 500,
    letterSpacing: '0em',
    fontVariantNumeric: 'tabular-nums',
  }),
  calPost: (clr) => ({
    background: 'transparent',
    borderRadius: r.xs,
    padding: '4px 8px',
    fontSize: 11,
    color: v.textSecondary,
    marginBottom: 4,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    borderLeft: `2px solid ${clr}`,
  }),

  // ── Code / info boxes ──────────────────────────────────────────────────────
  codeBlock: {
    ...surfaceInset,
    fontFamily: f.mono,
    fontSize: 12,
    color: v.textSecondary,
    whiteSpace: 'pre-wrap',
    overflowX: 'auto',
    lineHeight: 1.6,
  },
  infoBox: {
    ...surfaceInset,
    fontSize: 13,
    color: v.textSecondary,
    lineHeight: 1.6,
  },
  warnBox: {
    background: c.warningSoft,
    border: `1px solid rgba(224, 181, 80, 0.3)`,
    borderRadius: r.lg,
    padding: 16,
    fontSize: 13,
    color: c.warning,
    lineHeight: 1.6,
    marginBottom: 16,
  },
  errorBox: {
    background: c.errorSoft,
    border: `1px solid rgba(212, 114, 112, 0.32)`,
    borderRadius: r.lg,
    padding: 16,
    fontSize: 13,
    color: c.error,
    lineHeight: 1.6,
    marginBottom: 16,
  },
  helpDrawer: {
    ...surfaceInset,
  },

  // ── Toast ──────────────────────────────────────────────────────────────────
  // Glass surface + brand glow. Matches .studio-undo-toast.
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    background: 'rgba(28, 28, 31, 0.86)',
    backdropFilter: 'saturate(180%) blur(20px)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    border: `1px solid ${v.borderStrong}`,
    borderRadius: r.xl,
    padding: '12px 18px',
    fontSize: 13,
    color: c.brandText,
    zIndex: 1000,
    boxShadow: `${sh.floating}, ${sh.highlight}`,
  },

  // ── Progress ───────────────────────────────────────────────────────────────
  progressBar: {
    height: 6,
    background: 'rgba(180, 168, 150, 0.08)',
    borderRadius: r.pill,
    overflow: 'hidden',
    marginBottom: 10,
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.32)',
  },
  progressFill: (pct) => ({
    height: '100%',
    width: `${Math.min(100, pct)}%`,
    background: `linear-gradient(180deg, #de6235 0%, #c44e26 100%)`,
    borderRadius: r.pill,
    boxShadow:
      'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 0 12px rgba(212, 88, 45, 0.36)',
    transition: `width ${m.luxe} ${e.out}`,
  }),
  statBox: {
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    padding: '14px 16px',
    textAlign: 'center',
    boxShadow: sh.highlightSoft,
  },

  // ── Grids ──────────────────────────────────────────────────────────────────
  dashboardTopGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 0.72fr)',
    gap: 20,
    marginBottom: 18,
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.52fr) minmax(300px, 0.88fr)',
    gap: 20,
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(300px, 0.84fr) minmax(0, 1.16fr)',
    gap: 20,
    minHeight: 520,
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
  },

  // ── Metric tiles ───────────────────────────────────────────────────────────
  // UI elevation 2026-06-09: padding 18×20 (desktop), 16×18 (mobile), gap 12px
  metricTile: {
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    padding: '18px 20px',
    boxShadow: sh.highlightSoft,
    transition: `background ${m.base} ${e.out}, border-color ${m.base} ${e.out}`,
    gap: 12,
    display: 'grid',
    '@media (max-width: 768px)': {
      padding: '16px 18px',
    },
  },
  // UI elevation 2026-06-09: big metric value 17–24px per context, 700wt, -0.02em tracking
  metricValue: {
    fontSize: 22,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.02em',
    marginTop: 0,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
  },
  // UI elevation 2026-06-09: metric label → sans, metric label spec (sentence case)
  metricLabel: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.textTertiary,
    letterSpacing: '0em',
    textTransform: 'none',
    fontWeight: 600,
  },

  // ── Operational counters ───────────────────────────────────────────────────
  operationalCountersBand: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 20,
  },
  // UI elevation 2026-06-09: counter band padding 18×20 (desktop), 16×18 (mobile)
  counterBandItem: {
    padding: '18px 20px',
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    boxShadow: sh.highlightSoft,
    gap: 6,
    display: 'grid',
    '@media (max-width: 768px)': {
      padding: '16px 18px',
    },
  },
  // UI elevation 2026-06-09: counter band value → big metric spec (sentence case)
  counterBandValue: {
    fontSize: 22,
    fontWeight: 700,
    color: v.text,
    letterSpacing: '-0.02em',
    marginTop: 0,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
  },
  metricsBand: {
    ...surfaceBase,
    marginBottom: 20,
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 10,
  },
  metricBandItem: {
    padding: '8px 12px',
  },

  // ── Dashboard command surface ──────────────────────────────────────────────
  // The hero on the Dashboard. Marquee surface — gets the most lavish
  // treatment: ember radial glow, top sheen, brand-tinted border.
  dashboardCommandSurface: {
    position: 'relative',
    marginBottom: 18,
    padding: 32,
    background: `radial-gradient(900px 360px at 90% -20%, rgba(212, 88, 45, 0.08), transparent 60%), linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0) 30%), linear-gradient(135deg, ${v.surface0} 0%, ${v.canvas} 100%)`,
    border: `1px solid rgba(212, 88, 45, 0.22)`,
    borderRadius: r['2xl'],
    boxShadow: `${sh.card}, ${sh.highlight}, 0 0 40px rgba(212, 88, 45, 0.06)`,
    overflow: 'hidden',
  },
  dashboardCommandHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 22,
    flexWrap: 'wrap',
  },
  dashboardCommandCopy: {
    display: 'grid',
    gap: 8,
    maxWidth: 760,
  },
  dashboardCommandTitle: {
    fontFamily: f.editorial,
    fontSize: 32,
    lineHeight: 1.04,
    fontWeight: 600,
    color: v.text,
    letterSpacing: '-0.02em',
    fontFeatureSettings: '"ss01" 1, "kern" 1',
  },
  dashboardCommandSub: {
    fontSize: 14,
    color: v.textSecondary,
    lineHeight: 1.6,
    maxWidth: 720,
  },
  dashboardCommandActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
    minWidth: 240,
  },
  dashboardCommandGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  // UI elevation 2026-06-09: dashboard command metric padding 18×20 (desktop), 16×18 (mobile)
  dashboardCommandMetric: (tone = 'neutral') => {
    const toneStyles = getToneStyles(tone)
    return {
      display: 'grid',
      gap: 6,
      padding: '18px 20px',
      background: `linear-gradient(180deg, rgba(255, 255, 255, 0.022) 0%, rgba(255, 255, 255, 0) 70%), ${v.surfaceFlat}`,
      border: `1px solid ${toneStyles.borderColor}`,
      borderRadius: r.lg,
      boxShadow: sh.highlightSoft,
      '@media (max-width: 768px)': {
        padding: '16px 18px',
      },
    }
  },
  // UI elevation 2026-06-09: dashboard command value → sans, big metric spec (24px, sentence case)
  dashboardCommandValue: {
    fontFamily: f.body,
    fontSize: 24,
    fontWeight: 700,
    color: v.text,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
  },
  strategyStatusStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 1,
    overflow: 'hidden',
    border: `1px solid ${v.borderSubtle}`,
    borderRadius: r.lg,
    background: v.borderSubtle,
  },
  strategyStatusItem: {
    display: 'grid',
    gap: 5,
    padding: '12px 14px',
    background: v.bg,
    minWidth: 0,
  },
  strategyStatusValue: {
    fontSize: 13,
    color: v.textSecondary,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  dashboardGridPremium: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(300px, 0.85fr)',
    gap: 18,
  },
  dashboardSection: {
    padding: 22,
    background: v.surface0,
  },
  dashboardQuietState: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 0 0',
    borderTop: `1px solid ${v.borderSubtle}`,
  },

  // ── Lists and rows ─────────────────────────────────────────────────────────
  listRow: (active = false) => ({
    padding: '14px 18px',
    borderTop: `1px solid ${v.borderSubtle}`,
    background: active ? v.brandSoft : 'transparent',
    cursor: 'pointer',
    boxShadow: active ? `inset 2px 0 0 ${v.brand}` : 'none',
    transition: `background-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  feedList: {
    display: 'grid',
    gap: 6,
  },
  feedRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    textAlign: 'left',
    padding: '14px 0',
    borderTop: `1px solid ${v.borderSubtle}`,
    background: 'transparent',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    cursor: 'pointer',
    transition: `background-color ${m.quick} ${e.out}, transform ${m.quick} ${e.out}`,
  },
  railList: {
    display: 'grid',
    gap: 0,
  },
  railRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    padding: '12px 0',
    borderTop: `1px solid ${v.borderSubtle}`,
  },
  listToolbar: {
    padding: 18,
    borderBottom: `1px solid ${v.border}`,
    display: 'grid',
    gap: 12,
  },
  listEmptyCompact: {
    padding: '20px 18px',
    color: v.textTertiary,
  },
  dividedList: {
    display: 'grid',
  },
  dividedListItem: {
    padding: '12px 0',
    borderTop: `1px solid ${v.borderSubtle}`,
  },

  // ── Detail blocks ──────────────────────────────────────────────────────────
  detailBlock: {
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: `1px solid ${v.borderSubtle}`,
  },
  detailMetaStrip: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 14,
  },

  // ── Summary ────────────────────────────────────────────────────────────────
  summaryConsole: {
    display: 'grid',
    gap: 12,
  },
  summaryList: {
    display: 'grid',
    gap: 12,
  },
  summaryLine: {
    display: 'grid',
    gap: 4,
    paddingBottom: 12,
    borderBottom: `1px solid ${v.borderSubtle}`,
  },
  summaryLineValue: {
    fontSize: 13,
    color: v.textSecondary,
    lineHeight: 1.45,
  },
  primaryActionBar: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  primaryColumnStack: {
    display: 'grid',
    gap: 18,
    alignContent: 'start',
  },
  railStack: {
    display: 'grid',
    gap: 18,
    alignContent: 'start',
  },
  inlineMetaGroup: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },

  // ── Resume rows ────────────────────────────────────────────────────────────
  resumeRow: {
    display: 'grid',
    gridTemplateColumns: '112px minmax(0, 1fr) auto',
    gap: 16,
    padding: '12px 0',
    borderTop: `1px solid ${v.borderSubtle}`,
    alignItems: 'center',
  },

  // ── Shell chrome ───────────────────────────────────────────────────────────
  // Topbar search trigger gets the same surface treatment as the
  // command palette input to telegraph "this opens the palette".
  shellSearch: {
    flex: 1,
    maxWidth: 520,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    padding: '10px 14px',
    boxShadow: sh.highlightSoft,
  },
  shellSearchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: v.text,
    fontSize: 14,
    fontFamily: f.body,
  },
  shellKeycap: {
    fontFamily: f.mono,
    fontSize: 10,
    color: v.textTertiary,
    border: `1px solid ${v.border}`,
    borderRadius: r.xs,
    padding: '3px 7px',
    background: 'rgba(0, 0, 0, 0.18)',
    boxShadow: sh.highlightSoft,
    letterSpacing: '0.06em',
  },
  shellBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 8, 10, 0.66)',
    backdropFilter: 'blur(8px) saturate(140%)',
    WebkitBackdropFilter: 'blur(8px) saturate(140%)',
    border: 'none',
    zIndex: 30,
  },
  shellSearchTrigger: {
    flex: 1,
    maxWidth: 400,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    padding: '10px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: sh.highlightSoft,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}`,
  },
  shellSearchText: {
    flex: 1,
    fontSize: 13,
    color: v.textTertiary,
    letterSpacing: '-0.005em',
  },
  shellIdentityTag: {
    // UI elevation 2026-06-09 — identity chip → sans per §2.1, sentence case
    fontFamily: f.body,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0em',
    color: v.textTertiary,
    border: `1px solid ${v.border}`,
    borderRadius: r.pill,
    padding: '5px 10px',
    background: v.surfaceFlat,
    boxShadow: sh.highlightSoft,
  },
  shellCommandButton: {
    minHeight: 32,
    padding: '0 12px',
    borderRadius: r.md,
    background: v.surfaceFlat,
    color: v.textSecondary,
    border: `1px solid ${v.borderStrong}`,
    fontFamily: f.mono,
    fontSize: 10,
    letterSpacing: '0.06em',
    boxShadow: sh.highlightSoft,
  },
  commandList: {
    position: 'absolute',
    top: 64,
    left: 0,
    width: '100%',
    background: v.surface1,
    border: `1px solid ${v.borderStrong}`,
    borderRadius: r.xl,
    overflow: 'hidden',
    zIndex: 10,
    boxShadow: `${sh.elevated}, ${sh.highlight}`,
  },

  // ── Command palette ────────────────────────────────────────────────────────
  paletteBackdrop: {
    position: 'fixed',
    inset: 0,
    border: 'none',
    background:
      'radial-gradient(900px 600px at 50% 25%, rgba(212, 88, 45, 0.04), transparent 60%), rgba(8, 8, 10, 0.66)',
    backdropFilter: 'blur(10px) saturate(140%)',
    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
    zIndex: 60,
  },
  paletteRoot: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 72,
    zIndex: 61,
    pointerEvents: 'none',
  },
  paletteCard: {
    width: 'min(720px, calc(100vw - 28px))',
    ...surfaceElevated,
    borderRadius: r.xl,
    pointerEvents: 'auto',
    boxShadow: `${sh.floating}, ${sh.highlight}`,
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0) 30%), ${v.surface1}`,
  },
  paletteInputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 12,
    borderBottom: `1px solid ${v.border}`,
  },
  paletteInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: v.text,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: f.body,
    letterSpacing: '-0.005em',
  },
  paletteIcon: {
    fontFamily: f.mono,
    fontSize: 12,
    color: v.textTertiary,
  },
  paletteMetaRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    padding: '12px 0 10px',
  },
  paletteResults: {
    display: 'grid',
    gap: 4,
    maxHeight: '56vh',
    overflowY: 'auto',
    paddingTop: 10,
  },
  paletteRow: (active = false) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    padding: '12px 12px',
    background: active
      ? `linear-gradient(90deg, rgba(212, 88, 45, 0.14) 0%, rgba(212, 88, 45, 0.06) 100%)`
      : 'transparent',
    border: `1px solid ${active ? 'rgba(212, 88, 45, 0.18)' : 'transparent'}`,
    borderRadius: r.md,
    cursor: 'pointer',
    textAlign: 'left',
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}`,
  }),
  paletteRowText: {
    display: 'grid',
    gap: 3,
  },
  paletteEmpty: {
    padding: '20px 4px 10px',
    color: v.textTertiary,
    fontSize: 13,
  },

  // ── Create layout ──────────────────────────────────────────────────────────
  createLayout: {
    display: 'grid',
    gridTemplateColumns: '240px minmax(0, 1fr)',
    gap: 20,
    alignItems: 'start',
  },
  modeCard: (active) => ({
    width: '100%',
    textAlign: 'left',
    padding: '14px 14px',
    background: active ? v.brandSoft : 'transparent',
    border: `1px solid ${active ? 'rgba(212, 88, 45, 0.22)' : v.border}`,
    borderRadius: r.lg,
    marginBottom: 8,
    cursor: 'pointer',
    color: active ? v.text : v.textSecondary,
    boxShadow: active
      ? `inset 2px 0 0 ${v.brand}, ${sh.highlightSoft}`
      : sh.highlightSoft,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}`,
  }),
  createCanvas: {
    display: 'grid',
    gap: 16,
  },
  createDesk: {
    display: 'grid',
    gridTemplateColumns: '340px minmax(0, 1fr)',
    gap: 20,
    alignItems: 'start',
  },
  createSetupColumn: {
    display: 'grid',
    gap: 14,
  },
  createActionStrip: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  promptProvenanceStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))',
    gap: 1,
    overflow: 'hidden',
    border: `1px solid ${v.borderSubtle}`,
    borderRadius: r.lg,
    background: v.borderSubtle,
    marginBottom: 18,
  },
  promptProvenanceItem: {
    display: 'grid',
    gap: 5,
    padding: '11px 12px',
    background: v.bg,
    minWidth: 0,
  },
  promptProvenanceValue: {
    fontSize: 12.5,
    fontWeight: 600,
    color: v.textSecondary,
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  createStrategyPanel: {
    display: 'grid',
    gap: 10,
    paddingTop: 14,
    marginTop: 14,
    borderTop: `1px solid ${v.borderSubtle}`,
  },
  createStrategyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  createStep: {
    paddingBottom: 14,
    borderBottom: `1px solid ${v.borderSubtle}`,
  },
  createStepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  // UI elevation 2026-06-09: create step index → sans, metric label spec (sentence case)
  createStepIndex: {
    width: 24,
    height: 24,
    borderRadius: r.pill,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: v.surfaceFlat,
    border: `1px solid ${v.border}`,
    fontFamily: f.body,
    fontSize: 11,
    color: v.textTertiary,
    boxShadow: sh.highlightSoft,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    letterSpacing: '0em',
  },

  // ── Carousel workspace ────────────────────────────────────────────────────
  carouselSetupGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 16,
  },
  carouselRuleStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 1,
    overflow: 'hidden',
    border: `1px solid ${v.borderSubtle}`,
    borderRadius: r.lg,
    background: v.borderSubtle,
    marginBottom: 18,
  },
  carouselRuleItem: {
    display: 'grid',
    gap: 5,
    padding: '12px 14px',
    background: v.bg,
  },
  carouselResultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  carouselSlideStack: {
    display: 'grid',
    gap: 10,
  },
  carouselSlide: (color) => ({
    display: 'grid',
    gridTemplateColumns: '44px minmax(0, 1fr) auto',
    gap: 14,
    alignItems: 'flex-start',
    padding: 18,
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.018) 0%, rgba(255, 255, 255, 0) 60%), ${v.surface0}`,
    border: `1px solid ${v.border}`,
    borderLeft: `2px solid ${color}66`,
    borderRadius: r.lg,
    boxShadow: `${sh.card}, ${sh.highlightSoft}`,
  }),
  // UI elevation 2026-06-09: carousel slide index → sans, metric label spec (sentence case)
  carouselSlideIndex: {
    fontFamily: f.body,
    fontSize: 11,
    color: c.brandText,
    background: v.bg,
    border: `1px solid ${v.borderSubtle}`,
    borderRadius: r.sm,
    width: 34,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  },
  carouselSlideContent: {
    display: 'grid',
    gap: 8,
    minWidth: 0,
  },
  carouselSlideTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: v.text,
    lineHeight: 1.35,
    letterSpacing: '-0.025em',
  },
  carouselSlideBody: {
    fontSize: 13,
    color: v.textSecondary,
    lineHeight: 1.65,
  },
  // UI elevation 2026-06-09: carousel visual note → sans, timestamp spec (no transform)
  carouselVisualNote: {
    fontFamily: f.body,
    fontSize: 11,
    color: v.textTertiary,
    lineHeight: 1.6,
    fontWeight: 500,
    letterSpacing: '0em',
  },
  carouselDiagramFrame: {
    border: `1px solid ${v.border}`,
    borderRadius: r.lg,
    overflow: 'hidden',
    background: v.bg,
  },
  carouselSlideActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 8,
  },

  // ── Settings ───────────────────────────────────────────────────────────────
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: '240px minmax(0, 1fr)',
    gap: 20,
    alignItems: 'start',
  },
  settingsStack: {
    display: 'grid',
    gap: 14,
    alignContent: 'start',
  },
  settingsTabs: {
    display: 'grid',
    gap: 8,
    alignContent: 'start',
    position: 'sticky',
    top: 0,
  },
  settingsTab: (active) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 14px',
    borderRadius: r.lg,
    border: `1px solid ${active ? 'rgba(212, 88, 45, 0.22)' : v.border}`,
    background: active ? v.brandSoft : v.surfaceFlat,
    color: active ? v.text : v.textSecondary,
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: active
      ? `inset 2px 0 0 ${v.brand}, ${sh.highlightSoft}`
      : sh.highlightSoft,
    transition: `background ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}, color ${m.quick} ${e.out}`,
  }),
  settingsSection: {
    display: 'grid',
    gap: 14,
    paddingBottom: 20,
    marginBottom: 20,
    borderBottom: `1px solid ${v.borderSubtle}`,
  },
  settingActionRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 6,
  },
  settingsHelpToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 'none',
    color: v.textTertiary,
    cursor: 'pointer',
    padding: 0,
    fontSize: 13,
    fontWeight: 500,
  },

  // ── Side utility (legacy compat) ───────────────────────────────────────────
  sideUtilityBlock: { ...surfaceInset, marginTop: 14 },
  sideModule: { ...surfaceInset, display: 'grid', gap: 12 },
  sideUtilityRow: { paddingTop: 12, display: 'grid', gap: 4 },
  sideUtilityMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 12,
    borderTop: `1px solid ${v.borderSubtle}`,
  },
}

// ── Button helper ────────────────────────────────────────────────────────────
// Wave 47x: matches .studio-button (height-aligned padding, motion
// tokens). Brand variant gets the cast-metal gradient, outline gets
// the subtle surface fill.
//
// Wave 47x.2 — DEPRECATION NOTE: prefer the <Button> primitive from
// ./ui-kit/Button.jsx for new code. The two paths now produce the
// same visual output (CSS aligned to this helper's gradient + radius
// + transitions in studio.css), but the primitive carries proper
// ARIA, disabled/aria-disabled handling, and `as` polymorphism.
// btn() remains supported for the ~63 existing call sites — they
// render correctly and don't need urgent migration.

export function btn(variant) {
  const base = {
    padding: '8px 14px',
    borderRadius: r.md,
    fontFamily: f.body,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid transparent',
    letterSpacing: '-0.005em',
    transition: `background-color ${m.quick} ${e.out}, border-color ${m.quick} ${e.out}, color ${m.quick} ${e.out}, box-shadow ${m.quick} ${e.out}, transform ${m.instant} ${e.snap}`,
  }
  if (variant === 'primary') {
    return {
      ...base,
      background: `linear-gradient(180deg, #de6235 0%, #d4582d 55%, #c44e26 100%)`,
      color: '#1a0e05',
      borderColor: 'rgba(212, 88, 45, 0.55)',
      boxShadow: sh.brand,
    }
  }
  return {
    ...base,
    ...getButtonVariant(variant),
  }
}
